import './manageDictProposals.scss';
import TableActions from "../../../../components/shared/table/tableActions/tableActions";
import Table from "../../../../components/shared/table/table";
import ConfirmModal from "../../../../components/modalWindow/confirmModal";
import DictProposalActionSummary from "./dictProposalActionSummary/dictProposalActionSummary";
import { CellContext, createColumnHelper, Row } from "@tanstack/react-table";
import { TableProps, ThreeDotsTableTypes } from "../../../../components/shared/table/types/tableProps";
import { HeadersTable } from "../../../../helpers/featureTableConstruct";
import { useContext, useEffect, useState } from "react";
import { DictProposalActionSummaryProps } from "./dictProposalActionSummary/types/dictProposalActionSummaryProps";
import { ConfirmModalProps } from "../../../../components/modalWindow/types/confirmModalProps";
import { TableContext } from "../../../../contexts/tableContext";
import { ApproveDictProposalsProps, RejectDictProposalsProps } from "../../../../services/dictionariesService/types/dictionariesServiceProps";
import { apiContext } from "../../../../contexts/apiContext";
import { approveDictProposals, getDictProposals, rejectDictProposals } from "../../../../services/dictionariesService/dictionariesService";
import { notifyError } from "../../../../components/snackbar/snackbar";
import { getDictProposalsDetails, getDictionaryProposalOverview, getFoldoutContent } from "../../../../helpers/dictionaryProposals";
import { DictProposalActions, DictionaryProposal, DictionaryProposalDetails, DictionaryProposalOverview } from "./types/manageDictProposalsProps";
import { getDictionariesFromLayers, getDictionaryForCodedValues, getDictionaryForEditing } from '../../../../helpers/dictionariesFromLayers';
import { Option } from "./../../../../components/shared/selectInput/types/selectInputProps";
import { MutableDict } from '../manageDicts/types/manageDictsProps';

const ManageDictProposals = () => {
    const api = useContext(apiContext);
    const { checkboxesTable, reloadTable, setReloadTable, setCheckboxesTable } = useContext(TableContext); 
    const [rowsToFoldOut, setRowsToFoldOut] = useState<string[]>([]);
    const [visibilityRows, setVisibilityRows] = useState<string[]>([]);
    const [summary, setSummary] = useState<boolean>(false);
    const [summaryContent, setSummaryContent] = useState<DictionaryProposalDetails>({});
    const [confirmation, setConfirmation] = useState<boolean>(false);
    const [action, setAction] = useState<any>();
    const [selectedRow, setSelectedRow] = useState<any>();
    const [loading, setLoading] = useState<boolean>(true);
    const [dictProposals, setDictProposals] = useState<DictionaryProposalDetails>({});
    const [tableData, setTableData] = useState<DictionaryProposalOverview[]>([]);
    const [academicTitleDict, setAcademicTitleDict] = useState<Option[]>([]);
    const [publicationTypeDict, setPublicationTypeDict] = useState<Option[]>([]);
    
    useEffect(() => {
        setCheckboxesTable([]);
    }, [setCheckboxesTable])

    // Get academic title coded values
    useEffect(() => {
        var authorDictId = api.featureServices.dictionariesForCodedValues.find(item => item[0] ==="AUTORZY");
        var TypPublikacjiId = api.featureServices.dictionariesForEditing.find(item => item[0] ==="DICT_TYP_PUBLIKACJI");
        if (authorDictId) {
            getDictionaryForCodedValues(api.featureServices.dictionariesForEditingBaseUrl + authorDictId[1])
            .then((res) => {
                var dictId: number = +authorDictId![1];
                var codedValues = getDictionariesFromLayers(res, [dictId])["TYTUL"];
                setAcademicTitleDict(codedValues);
            })
            .catch(() => {
                notifyError({ message: "Próba pobrania danych zakończona niepowodzeniem" });
            })
        }
        if(TypPublikacjiId){
            getDictionaryForEditing(api.featureServices.dictionariesForEditingBaseUrl + TypPublikacjiId[1])
            .then((res: any) => {
                var data: MutableDict[] = res;
                var dictOptions: Option[] = [];
                data.sort((a, b) => a.KOLEJNOSC! - b.KOLEJNOSC!);
                data.forEach(item => {
                    var dictOption: Option = { name: item.WARTOSC, value: item.ID};
                    dictOptions.push(dictOption);
                })
                setPublicationTypeDict(dictOptions);
            })
            .catch(() => {
                notifyError({ message: "Próba pobrania danych zakończona niepowodzeniem" });
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Table
    useEffect(() => {       
        getDictProposals({apiBase: api.apiUrl + api.apiVersion})
            .then((response: any) => {
                setDictProposals(response);
                prepareTableData(response);
            })
            .catch(() => {
                notifyError({ message: "Próba pobrania propozycji słownikowych zakończona niepowodzeniem" });
            })

    }, [api, reloadTable]);

    const prepareTableData = (details: DictionaryProposalDetails) => {       
        var overview = getDictionaryProposalOverview(details);
        setTableData(overview);
        setLoading(false);
    }

    const approveDictProposalOnClick = (row?: any) => {
        setAction(DictProposalActions.Approve);
        if (row) setSelectedRow(row);
        setConfirmation(true);        
    }

    const rejectDictProposalOnClick = (row?: any) => {
        setAction(DictProposalActions.Reject);
        if (row) setSelectedRow(row);
        setConfirmation(true); 
    }

    const cancel = () => {
        setConfirmation(false);
    }

    const confirm = () => {
        setLoading(true);
        var currentSelectedProposals: DictionaryProposal[] = [];
        if (selectedRow) {
            currentSelectedProposals.push(selectedRow.proposalObject);
        }
        else 
        {
            checkboxesTable.forEach(item => currentSelectedProposals.push(item.original.proposalObject));
        }
        action === DictProposalActions.Approve ? approveProposals(currentSelectedProposals) : rejectProposals(currentSelectedProposals);
        setConfirmation(false);
    }

    const approveProposals = (selectedProposals: DictionaryProposal[]) => {       
        setSelectedRow(undefined);
        
        var approveDictProposalsProps: ApproveDictProposalsProps= {
            dictProposals: selectedProposals,
            apiBase: api.apiUrl + api.apiVersion
        }

        approveDictProposals(approveDictProposalsProps)
        .then((response: any) => {
            proposalActionSummary(response);
            setLoading(false);
        })
        .catch(() => {
            notifyError({ message: "Próba zatwierdzenia propozycji wartości słownikowych zakończyła się niepowodzeniem." });
            setReloadTable(!reloadTable);
            setLoading(false);
        })
    }

    const rejectProposals = (selectedProposals: DictionaryProposal[]) => {
        setSelectedRow(undefined);

        var selectedProposalsIds: number[] = []; 
        selectedProposals.forEach(item => {
            selectedProposalsIds.push(item.id);
        });

        var rejectDictProposalsProps: RejectDictProposalsProps = {
            dictProposalIds: selectedProposalsIds,
            apiBase: api.apiUrl + api.apiVersion
        }
        
        rejectDictProposals(rejectDictProposalsProps)
        .then(() => {
            var rejectedDetails = getDictProposalsDetails(selectedProposals, dictProposals);
            proposalActionSummary(rejectedDetails)
            setLoading(false);

        })
        .catch(() => {
            notifyError({ message: "Próba odrzucenia propozycji wartości słownikowych zakończyła się niepowodzeniem." });
            setReloadTable(!reloadTable);
            setLoading(false);
        })
    }

    const proposalActionSummary = (actionRecords: DictionaryProposalDetails) => {
        setReloadTable(!reloadTable);
        setSummary(true);

        actionRecords.authorsDict = dictProposals.authorsDict;
        actionRecords.institutionsDict = dictProposals.institutionsDict;
        setSummaryContent(actionRecords);
    } 
    
    const cellAction = (properties: CellContext<any, unknown>) => {
        return (<TableActions
            row={properties.row}
            objectType={"manageDictProposals"}
            doNotDisturbOnClickHandler={rejectDictProposalOnClick}
            checkCircleClickHandler={approveDictProposalOnClick}
            visibilityClickHandler={handleVisibilityClick}
            visibilityRows={visibilityRows}
            closeClickHandler={handleCloseClick}
        />)
    }

    const handleVisibilityClick = (row: any) => {
        setRowsToFoldOut([...rowsToFoldOut, row.proposalObject.id]);  
        setVisibilityRows([...visibilityRows, row.proposalObject.id]);        
    }

    const handleCloseClick = (row: any) => {
        setRowsToFoldOut([...rowsToFoldOut.filter((item: any) => item !== row.proposalObject.id)]);  
        setVisibilityRows([...visibilityRows.filter((item: any) => item !== row.proposalObject.id)]);
    }

    const prepareFoldoutContent = (row: any) => {  
        return getFoldoutContent(row, dictProposals, academicTitleDict, publicationTypeDict);
    }

    const foldoutParamToCheck = (row: any) => {
        return row.proposalObject.id;
    }

    let manageDictProposalsHeaders = {
        date: "Data",
        userName: "Nazwa użytkownika",
        dictName: "Nazwa słownika",
        description: "Propozycja nowej wartości"
    };

    const compareId = (row1: Row<any>, row2: Row<any>) => {
        return row1.original.proposalObject.id === row2.original.proposalObject.id;
    }

    var tableProps: TableProps = {
        columnAliases: manageDictProposalsHeaders,
        columnHelper: createColumnHelper<HeadersTable>(),
        columnNames: Object.keys(manageDictProposalsHeaders),
        cellActions: cellAction,
        loading: loading,
        data: tableData,
        firstColumnCheckbox: true,
        compareId: compareId,
        tableFiltersVisible: false,
        foldoutRows: true,
        rowsToFoldOut: rowsToFoldOut,
        foldoutContent: prepareFoldoutContent,
        foldoutParamToCheck: foldoutParamToCheck,
        threeDots: true,
        threeDotsProps: {
            menuItemClass: 'threeDotsMenuItemDictProposals',
            table: ThreeDotsTableTypes.DictionaryProposals,
            approveDictProposalOnClick: approveDictProposalOnClick,
            rejectDictProposalOnClick: rejectDictProposalOnClick
        }
    }

    var confirmationProps: ConfirmModalProps = {
        width: "660px",
        height: "auto",
        confirmTitle: action === DictProposalActions.Approve ? "Zatwierdź" : "Odrzuć",
        onConfirm: confirm,
        cancelTitle: "Anuluj",
        onCancel: cancel,
        question: action === DictProposalActions.Approve ?
            "Czy na pewno chcesz zatwierdzić propozycje wartości słownikowych? Rekordy zostaną dodane do odpowiednich słowników i nie będą już widoczne w tabeli propozycji." :
            "Czy na pewno chcesz odrzucić propozycje wartości słownikowych? Rekordy nie będą już widoczne w tabeli."
    }

    const handleSummaryClose = () => {
        setSummary(false);
        setCheckboxesTable([]);
    }

    var dictProposalActionSummaryProps: DictProposalActionSummaryProps = {
        action: action,
        onClose: handleSummaryClose,
        data: summaryContent
    }

    return (
        <>
            <div className="DictProposalsTabView">
                <Table {...tableProps} />
                {confirmation && <ConfirmModal {...confirmationProps}/>}
                {summary && <DictProposalActionSummary {...dictProposalActionSummaryProps}/>}
            </div>
        </>
    );
}

export default ManageDictProposals;